import { CopyContext } from '@root/types/copyTracking';

export class CopyTrackingService {
  private static instance: CopyTrackingService;
  private copyContext: CopyContext | null = null;
  private onContextChangeCallbacks: ((context: CopyContext | null) => void)[] = [];
  private onOpenViperAICallbacks: ((context: CopyContext) => void)[] = [];

  private constructor() {}

  static getInstance(): CopyTrackingService {
    if (!CopyTrackingService.instance) {
      CopyTrackingService.instance = new CopyTrackingService();
    }
    return CopyTrackingService.instance;
  }

  // Get current breadcrumb from window object, DOM, or URL
  private getCurrentBreadcrumb(): string {
    try {
      // Try to get breadcrumb from window object first
      if (window.BREADCRUMB_PROPS?.breadcrumbs) {
        const breadcrumbs = window.BREADCRUMB_PROPS.breadcrumbs;
        return breadcrumbs.map((b: any) => b.title).join(' > ');
      }

      // Try to get breadcrumb from DOM elements
      const breadcrumbElement = document.querySelector('.Breadcrumb, .breadcrumb, [class*="breadcrumb"]');
      if (breadcrumbElement) {
        const breadcrumbText = breadcrumbElement.textContent?.trim();
        if (breadcrumbText && breadcrumbText.length > 0) {
          return breadcrumbText;
        }
      }

      // Try to get from page title or header
      const pageTitle = document.querySelector('h1, .page-title, .main-title');
      if (pageTitle) {
        const titleText = pageTitle.textContent?.trim();
        if (titleText && titleText.length > 0) {
          return titleText;
        }
      }

      // Fallback to URL-based breadcrumb detection
      const path = window.location.pathname;
      const pathSegments = path.split('/').filter(segment => segment);
      
      if (pathSegments.length === 0) return 'Dashboard';
      
      // Map common paths to readable names
      const pathMapping: { [key: string]: string } = {
        'dashboard': 'Dashboard',
        'environments': 'Environments',
        'compliance': 'Compliance',
        'rules': 'Rules',
        'risk': 'Risk',
        'assets': 'Assets',
        'remediation': 'Remediation',
        'scan-history': 'Scan History',
        'inventory': 'Inventory',
        'identity': 'Identity',
        'kubernetes': 'Kubernetes',
        'events': 'Events',
        'notifications': 'Notifications',
        'profile': 'Profile',
        'admin': 'Admin'
      };

      const breadcrumbParts = pathSegments.map(segment => {
        return pathMapping[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
      });

      return breadcrumbParts.join(' > ');
    } catch (error) {
      console.warn('Error getting breadcrumb:', error);
      return 'Unknown Page';
    }
  }

  // Get card or table name from DOM context
  private getCardOrTableName(element: HTMLElement): string {
    try {
      // Look for common card/table identifiers in the DOM hierarchy
      let currentElement: HTMLElement | null = element;
      
      while (currentElement && currentElement !== document.body) {
        // Check for table headers
        const tableHeader = currentElement.querySelector('thead th, .table-header, .card-title, .panel-title, .MuiDataGrid-columnHeader');
        if (tableHeader) {
          const headerText = tableHeader.textContent?.trim();
          if (headerText && headerText.length > 0) {
            return headerText;
          }
        }

        // Check for card titles
        const cardTitle = currentElement.querySelector('.card-title, .panel-title, .content-title, h1, h2, h3, .title, .header-title');
        if (cardTitle) {
          const titleText = cardTitle.textContent?.trim();
          if (titleText && titleText.length > 0) {
            return titleText;
          }
        }

        // Check for data attributes
        const dataTitle = currentElement.getAttribute('data-title') || 
                         currentElement.getAttribute('data-card-name') ||
                         currentElement.getAttribute('data-table-name') ||
                         currentElement.getAttribute('aria-label');
        if (dataTitle) {
          return dataTitle;
        }

        // Check for class names that might indicate table/card names
        const className = currentElement.className;
        if (className.includes('table') || className.includes('card') || className.includes('panel') || 
            className.includes('grid') || className.includes('list') || className.includes('container')) {
          const textContent = currentElement.textContent?.trim();
          if (textContent && textContent.length < 100 && textContent.length > 3) {
            return textContent.split('\n')[0].trim();
          }
        }

        // Check if current element itself has meaningful text
        if (currentElement.textContent && currentElement.textContent.trim().length > 0 && 
            currentElement.textContent.trim().length < 50) {
          const text = currentElement.textContent.trim();
          // Skip if it's just numbers or very short text
          if (text.length > 3 && !/^\d+$/.test(text)) {
            return text;
          }
        }

        currentElement = currentElement.parentElement;
      }

      // Final fallback - try to get from page context
      const pageContext = document.querySelector('.page-title, .main-title, h1, .content-header');
      if (pageContext) {
        const contextText = pageContext.textContent?.trim();
        if (contextText && contextText.length > 0) {
          return contextText;
        }
      }

      return 'Content';
    } catch (error) {
      console.warn('Error getting card/table name:', error);
      return 'Content';
    }
  }

  // Track copy operation
  public trackCopy(copiedText: string, sourceElement?: HTMLElement): void {
    const breadcrumb = this.getCurrentBreadcrumb();
    let cardOrTableName = 'Content';
    
    if (sourceElement) {
      cardOrTableName = this.getCardOrTableName(sourceElement);
    } else {
      // Try to get card/table name from current page context when no source element
      cardOrTableName = this.getCardOrTableNameFromPage();
    }
    
    // Debug logging
    console.log('Copy tracking - Breadcrumb:', breadcrumb);
    console.log('Copy tracking - Card/Table Name:', cardOrTableName);
    console.log('Copy tracking - Copied Text:', copiedText);
    
    const context: CopyContext = {
      breadcrumb,
      cardOrTableName,
      copiedText,
      timestamp: Date.now()
    };

    this.copyContext = context;
    this.notifyContextChange();
  }

  // Get card or table name from page context when no source element
  private getCardOrTableNameFromPage(): string {
    try {
      // Look for common page elements that might indicate the current section
      const selectors = [
        '.page-title', '.main-title', '.content-title', '.section-title',
        'h1', 'h2', '.card-title', '.panel-title', '.table-title',
        '.MuiDataGrid-root .MuiDataGrid-columnHeader',
        '[class*="title"]', '[class*="header"]'
      ];
      
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) {
          const text = element.textContent?.trim();
          if (text && text.length > 0 && text.length < 100) {
            return text;
          }
        }
      }
      
      return 'Content';
    } catch (error) {
      console.warn('Error getting card/table name from page:', error);
      return 'Content';
    }
  }

  // Get current copy context
  public getCopyContext(): CopyContext | null {
    return this.copyContext;
  }

  // Clear copy context
  public clearCopyContext(): void {
    this.copyContext = null;
    this.notifyContextChange();
  }

  // Subscribe to context changes
  public onContextChange(callback: (context: CopyContext | null) => void): () => void {
    this.onContextChangeCallbacks.push(callback);
    return () => {
      const index = this.onContextChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.onContextChangeCallbacks.splice(index, 1);
      }
    };
  }

  // Subscribe to ViperAI open requests
  public onOpenViperAI(callback: (context: CopyContext) => void): () => void {
    this.onOpenViperAICallbacks.push(callback);
    return () => {
      const index = this.onOpenViperAICallbacks.indexOf(callback);
      if (index > -1) {
        this.onOpenViperAICallbacks.splice(index, 1);
      }
    };
  }

  // Open ViperAI with current context
  public openViperAIWithContext(): void {
    if (this.copyContext) {
      this.onOpenViperAICallbacks.forEach(callback => callback(this.copyContext!));
    }
  }

  // Notify context change
  private notifyContextChange(): void {
    this.onContextChangeCallbacks.forEach(callback => callback(this.copyContext));
  }
}

export const copyTrackingService = CopyTrackingService.getInstance();
